/**
 * 草稿页的参考照片（TEACH-UNDERSTAND-UI-117；接口照 adm1 TEACH-WIRE-117 HTTP-CONTRACT 第一节）：每单最多 3 张，能删；视频帧进 118（I 10-17）。
 * - 只在草稿页传：新建草稿时 refs 只能是空的，参考一律走上传口；草稿里的 refs 只用服务端回来的这一单的 refs，前端不自己记、不自己拼。
 * - 传一张：multipart（file、kind=photo），带幂等键——同一张照片（名字、大小、修改时间一样）重发沿用同一个键，换了照片才换键；
 *   回来的单 refs 多一个号、draft_revision 加一、回到草稿（之前整理好的说明作废），写进缓存。
 * - 删一张：按媒体号删，回来的单写进缓存；删了焦点回到「放一张照片」上（列表里那一格没了）。
 * - 看得到的小图：只有这一次打开页面时刚传的那几张有（本地的图）；服务端没有给主人看参考原图的读口，刷新以后照实写「参考照片 1」，不编图。
 * - 上传前那一句照实说用处和去向：只用来教 TA 这个动作、不会公开，教完就删，主人也可以先删（原图在这一单教完后整单删掉，adm1）。
 * - 类型不对（不是 JPG、PNG、WebP）当场说，不发请求；太大、放满了、已经确认过由服务端说了算，照它的理由各说一句。
 * - 文件框本身藏起来（hidden，不进 Tab 顺序、不算点按区），由「放一张照片」那颗键点开。
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Icon } from "@/shared/ui";
import {
  REFS_ACCEPT, REFS_ADD, REFS_ADDED, REFS_BAD_TYPE, REFS_CONFIRMED, REFS_FULL, REFS_LEAD, REFS_MAX, REFS_PRIVACY, REFS_REMOVED, REFS_TITLE, REFS_TOO_LARGE,
  refsPhoto, refsRemove,
} from "./teachModel";
import type { TeachOrder } from "./teachTypes";
import { teachKeys, useTeachApi } from "./useTeach";
import { TeachButton } from "./TeachButton";
import "./teach-understand.css";

const ACCEPTED: ReadonlySet<string> = new Set(REFS_ACCEPT.split(","));

/** 服务端拒了这张照片：照它给的理由各说一句；认不出的照全站的说法 */
export function refsErrorText(error: unknown): string {
  const e = toApiError(error);
  const details = (e.details ?? {}) as { reason?: string; why?: string };
  if (details.reason === "reference_rejected") {
    if (details.why === "too_many") return REFS_FULL;
    if (details.why === "bad_type") return REFS_BAD_TYPE;
    if (details.why === "too_large") return REFS_TOO_LARGE;
  }
  if (details.reason === "already_confirmed") return REFS_CONFIRMED;
  return e.playerMessage;
}

export function TeachRefs({ order }: { order: TeachOrder }) {
  const api = useTeachApi()!;
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const refs = order.refs ?? [];
  const previews = useRef(new Map<string, string>());
  const keyFor = useRef<{ sig: string; key: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const [note, setNote] = useState<string | null>(null);
  const [badType, setBadType] = useState(false);
  // 删了一张以后把焦点放回「放一张照片」：放满时那颗键原来不在，要等新的单写进页面、这一轮画完才有（每次画完看一眼）
  const focusAdd = useRef(false);
  useEffect(() => {
    if (focusAdd.current && addButton.current) {
      focusAdd.current = false;
      addButton.current.focus();
    }
  });

  useEffect(() => {
    const urls = previews.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const put = (next: TeachOrder) => {
    queryClient.setQueryData(teachKeys.order(userId, next.order_id), next);
    void queryClient.invalidateQueries({ queryKey: teachKeys.orders(userId, order.pet_id) });
  };
  const add = useMutation({
    mutationFn: (file: File) => {
      const sig = `${file.name}|${file.size}|${file.lastModified}`;
      if (!keyFor.current || keyFor.current.sig !== sig) keyFor.current = { sig, key: newIdempotencyKey("teach-ref") };
      return api.addPhoto(order.order_id, file, keyFor.current.key);
    },
    onSuccess: (result, file) => {
      keyFor.current = null;
      if (typeof URL.createObjectURL === "function") previews.current.set(result.media_id, URL.createObjectURL(file));
      put(result.order);
      setNote(REFS_ADDED);
    },
  });
  const remove = useMutation({
    mutationFn: (mediaId: string) => api.removeReference(order.order_id, mediaId),
    onSuccess: (next, mediaId) => {
      const url = previews.current.get(mediaId);
      if (url) URL.revokeObjectURL(url);
      previews.current.delete(mediaId);
      put(next);
      setNote(REFS_REMOVED);
      focusAdd.current = true;
    },
  });
  const full = refs.length >= REFS_MAX;
  const busy = add.isPending || remove.isPending;
  const error = badType ? REFS_BAD_TYPE : add.error ? refsErrorText(add.error) : remove.error ? refsErrorText(remove.error) : null;

  return (
    <section className="ps-teach-card ps-teach-refs" aria-labelledby="teach-refs-title" data-teach-refs>
      <h3 id="teach-refs-title" className="ps-teach-group__title">
        <Icon name="camera" size={18} className="ps-teach-group__icon" />
        <span>{REFS_TITLE}</span>
      </h3>
      <p className="ps-teach-note">{REFS_LEAD}</p>
      {refs.length ? (
        <ul className="ps-teach-refs__list">
          {refs.map((mediaId, index) => {
            const url = previews.current.get(mediaId);
            return (
              <li key={mediaId} className="ps-teach-refs__item" data-ref={mediaId}>
                {url ? <img className="ps-teach-refs__photo" src={url} alt={refsPhoto(index + 1)} /> : (
                  <span className="ps-teach-refs__photo ps-teach-refs__photo--blank" role="img" aria-label={refsPhoto(index + 1)}>
                    <Icon name="camera" size={22} />
                  </span>
                )}
                <TeachButton variant="ghost" className="ps-teach-refs__remove" aria-label={refsRemove(index + 1)} busy={remove.isPending && remove.variables === mediaId} unavailable={busy} onClick={() => remove.mutate(mediaId)}>
                  <Icon name="close" size={18} />
                </TeachButton>
              </li>
            );
          })}
        </ul>
      ) : null}
      <p className="ps-teach-refs__privacy" id="teach-refs-privacy">{REFS_PRIVACY}</p>
      {full ? <p className="ps-teach-note" data-refs-full>{REFS_FULL}</p> : (
        <>
          <input
            ref={input}
            type="file"
            accept={REFS_ACCEPT}
            hidden
            data-refs-input
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              event.target.value = "";
              if (!file) return;
              setNote(null);
              if (!ACCEPTED.has(file.type)) {
                setBadType(true);
                return;
              }
              setBadType(false);
              add.mutate(file);
            }}
          />
          <TeachButton buttonRef={addButton} variant="secondary" block busy={add.isPending} unavailable={busy} describedBy="teach-refs-privacy" onClick={() => input.current?.click()}>
            <Icon name="camera" size={18} />
            {REFS_ADD}
          </TeachButton>
        </>
      )}
      {error ? <p className="ps-teach-form__error" role="alert">{error}</p> : null}
      {note && !error ? <p className="ps-teach-note" role="status">{note}</p> : null}
    </section>
  );
}
